import { registerQuery, useQuery } from 'data-fetching-client';
import enviro from 'enviro';
import idpAuthApiClient from 'hub-http-janus/clients/idpAuthApiClient';
import userInfo from 'hubspotter-http/hubSpotterInfo';
import { createRpcClientV2 } from 'rpc-client-utils';
import { useGetOAuthUrlQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc-app.hooks';
import {
  getOAuthUrlRpc,
  type GetOAuthUrlResponseType,
} from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc-internal';

const QA_PORTAL_ID = 99166168;
const PROD_PORTAL_ID = 521324;

const isQa = () => enviro.isQa();

async function getOAuthUrlInternal({
  listingId,
  published,
}: {
  listingId: number;
  published?: boolean;
}): Promise<GetOAuthUrlResponseType> {
  const chirpClient = createRpcClientV2({ hubHttpClient: idpAuthApiClient });

  const { hubspotter: user } = await userInfo();
  const userId = Number(user.employeeId) || 0;

  return chirpClient.call(getOAuthUrlRpc, {
    request: { listingId, published },
    portalId: isQa() ? QA_PORTAL_ID : PROD_PORTAL_ID,
    userId,
  });
}

const GET_OAUTH_URL_INTERNAL = registerQuery({
  fieldName: 'oauthUrlInternal',
  args: ['listingId', 'published'],
  fetcher: getOAuthUrlInternal,
});

export interface UseGetOAuthUrlProps {
  listingId?: number;
  published?: boolean;
  skip?: boolean;
  useInternalAuth?: boolean;
}

export function useGetOAuthUrl({
  listingId,
  published,
  skip = false,
  useInternalAuth = false,
}: UseGetOAuthUrlProps) {
  const {
    data: appAuthData,
    loading: appAuthLoading,
    error: appAuthError,
    refetch: appAuthRefetch,
  } = useGetOAuthUrlQuery({
    variables: {
      listingId: listingId!,
      published,
    },
    skip: skip || !listingId || useInternalAuth,
  });

  const {
    data: internalData,
    loading: internalLoading,
    error: internalError,
    refetch: internalRefetch,
  } = useQuery(GET_OAUTH_URL_INTERNAL, {
    variables: { listingId: listingId!, published },
    skip: skip || !listingId || !useInternalAuth,
  });

  const data = useInternalAuth ? internalData?.oauthUrlInternal : appAuthData;
  const loading = useInternalAuth ? internalLoading : appAuthLoading;
  const error = useInternalAuth ? internalError : appAuthError;
  const refetch = useInternalAuth ? internalRefetch : appAuthRefetch;

  return {
    data,
    loading,
    error,
    refetch,
  };
}
